import React, { useEffect, useState } from 'react';
import { assistantAPI } from '../../services/assistant';
import '../../styles/Assistant.css';

const describeError = (err, fallback) => err?.response?.data?.error || err?.message || fallback;
const BLANK = { term: '', meaning: '', synonyms: '' };
const split = (text) => String(text || '').split(',').map((x) => x.trim()).filter(Boolean);

/**
 * Company admins: the glossary the AI assistant uses in every conversation,
 * and the entries people suggested from their conversations, to approve or
 * reject.
 */
export default function AssistantGlossary({ companyId }) {
  const [entries, setEntries] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    try {
      setEntries((await assistantAPI.glossary(companyId)).data || []);
    } catch (err) {
      setError(describeError(err, 'Could not read the glossary'));
    }
  };
  useEffect(() => {
    if (companyId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const run = async (work, fallback) => {
    setSaving(true);
    try {
      await work();
      setError('');
      await load();
      return true;
    } catch (err) {
      setError(describeError(err, fallback));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    // Measures an entry is about are linked by the AI assistant when it saves an entry; an edit here keeps them.
    const entry = { term: form.term, meaning: form.meaning, synonyms: split(form.synonyms) };
    const ok = await run(
      () => (editingId ? assistantAPI.updateGlossary(companyId, editingId, entry) : assistantAPI.addGlossary(companyId, entry)),
      'Could not save the entry',
    );
    if (ok) { setForm(BLANK); setEditingId(null); }
  };

  const edit = (entry) => {
    setEditingId(entry.id);
    setForm({ term: entry.term, meaning: entry.meaning, synonyms: entry.synonyms.join(', ') });
  };
  const cancelEdit = () => { setEditingId(null); setForm(BLANK); };

  if (!entries) return error ? <div className="error-message" role="alert">{error}</div> : null;
  const suggested = entries.filter((e) => e.status === 'suggested');
  const approved = entries.filter((e) => e.status === 'approved');

  return (
    <div className="ai-glossary">
      <h4>Glossary</h4>
      <p className="settings-help">
        What your company&apos;s words mean, such as &quot;department means the Sage HR team&quot;. The AI assistant uses these in every conversation instead of asking.
        You can also note a caveat or open question about a figure, such as &quot;Post-release defects: the definition is not agreed yet&quot;; the AI assistant mentions it whenever it uses that figure.
      </p>

      {suggested.length > 0 && (
        <div className="ai-glossary-suggested">
          <p className="ai-glossary-heading">Suggested in conversations ({suggested.length})</p>
          {suggested.map((e) => (
            <div key={e.id} className="ai-glossary-entry ai-glossary-entry--suggested">
              <div>
                <strong>{e.term}</strong>{e.synonyms.length > 0 && <span className="ai-muted"> (also: {e.synonyms.join(', ')})</span>}
                <p>{e.meaning}</p>
                <span className="ai-muted">Suggested by {e.createdBy || 'a user'}</span>
              </div>
              <div className="ai-glossary-actions">
                <button type="button" className="ai-button ai-button--primary" disabled={saving} onClick={() => run(() => assistantAPI.approveGlossary(companyId, e.id), 'Could not approve it')}>Approve</button>
                <button type="button" className="ai-button ai-button--ghost" disabled={saving} onClick={() => edit(e)}>Edit</button>
                <button type="button" className="ai-button ai-button--ghost" disabled={saving} onClick={() => run(() => assistantAPI.removeGlossary(companyId, e.id), 'Could not reject it')}>Reject</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {approved.length > 0 ? (
        <div className="ai-glossary-list">
          {approved.map((e) => (
            <div key={e.id} className="ai-glossary-entry">
              <div>
                <strong>{e.term}</strong>{e.synonyms.length > 0 && <span className="ai-muted"> (also: {e.synonyms.join(', ')})</span>}
                <p>{e.meaning}</p>
                {e.members.length > 0 && <span className="ai-muted">About: {e.members.map((m) => <code key={m}>{m}</code>)}</span>}
              </div>
              <div className="ai-glossary-actions">
                <button type="button" className="ai-button ai-button--ghost" disabled={saving} onClick={() => edit(e)}>Edit</button>
                <button type="button" className="ai-button ai-button--ghost" disabled={saving} onClick={() => run(() => assistantAPI.removeGlossary(companyId, e.id), 'Could not delete it')} aria-label={`Delete ${e.term}`}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      ) : <p className="settings-help">The glossary is empty.</p>}

      <form className="ai-glossary-form" onSubmit={save}>
        <p className="ai-glossary-heading">{editingId ? 'Change the entry' : 'Add an entry'}</p>
        <label>Term<input value={form.term} maxLength={120} disabled={saving} onChange={(e) => setForm({ ...form, term: e.target.value })} placeholder="Department" /></label>
        <label>Meaning<textarea value={form.meaning} maxLength={1000} rows={2} disabled={saving} onChange={(e) => setForm({ ...form, meaning: e.target.value })} placeholder="The team an employee belongs to in Sage HR." /></label>
        <label>Other words for it <small>(optional, separated by commas)</small><input value={form.synonyms} disabled={saving} onChange={(e) => setForm({ ...form, synonyms: e.target.value })} placeholder="dept, team" /></label>
        <div className="ai-glossary-actions">
          <button type="submit" className="ai-button ai-button--primary" disabled={saving || !form.term.trim() || !form.meaning.trim()}>{editingId ? 'Save changes' : 'Add to glossary'}</button>
          {editingId && <button type="button" className="ai-button ai-button--ghost" onClick={cancelEdit}>Cancel</button>}
        </div>
      </form>
      {error && <div className="error-message" role="alert">{error}</div>}
    </div>
  );
}
